import * as React from 'react'
import classNames from 'classnames'
import { IconDefinition, faTimes } from '@fortawesome/pro-light-svg-icons'

import { Icon } from '@espoon-voltti/react-components/elements'

interface Props {
  children: React.ReactNode
}

type MenuState = boolean

interface MenuCtx {
  open: boolean
  setState: (v: boolean) => void
}

const Ctx = React.createContext<MenuCtx>({
  open: false,
  setState: () => {
    // void
  }
})

const Container = React.memo(({ children }: Props) => {
  const [state, setState] = React.useState<MenuState>(false)
  const handleStateUpdate = (v: boolean) => setState(v)

  return (
    <Ctx.Provider
      value={{
        open: state,
        setState: handleStateUpdate
      }}
    >
      <div className="context-menu-wrapper">{children}</div>
    </Ctx.Provider>
  )
})

interface TriggerProps {
  children: React.ReactNode
  dataQa: string
}

const Trigger = ({ children, dataQa }: TriggerProps) => {
  const ctx = React.useContext(Ctx)
  const handleTriggerClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    ctx.setState(!ctx.open)
  }

  return (
    <div onClick={handleTriggerClick} data-qa={dataQa}>
      {children}
    </div>
  )
}

const Items = ({ children }: Props) => {
  const ctx = React.useContext(Ctx)

  const closeContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    ctx.setState(false)
  }

  return ctx.open ? (
    <>
      <div
        className="context-menu-overlay"
        data-qa={'context-overlay'}
        onClick={closeContextMenu}
      />
      <div className="context-menu-items-container" onClick={closeContextMenu}>
        <div className="context-menu-items-icon-wrapper">
          <Icon icon={faTimes} />
        </div>
        {children}
      </div>
    </>
  ) : null
}

interface ContextMenuItemProps {
  children: React.ReactNode
  icon: IconDefinition
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  dataQa: string
  disabled?: boolean
}

const Item = ({
  children,
  onClick,
  icon,
  dataQa,
  disabled
}: ContextMenuItemProps) => {
  const ctx = React.useContext(Ctx)

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (!disabled) {
      ctx.setState(false)
      onClick(e)
    }
  }

  return (
    <button
      className={classNames('context-menu-item-container', {
        'is-disabled': disabled
      })}
      onClick={handleClick}
      data-qa={dataQa}
    >
      <Icon icon={icon} />
      {children}
    </button>
  )
}

export const ContextMenu = {
  Container,
  Trigger,
  Items,
  Item
}
